import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  User, MessageSquare, UserCheck, Lock, ExternalLink, Gift,
  CheckCircle2, Loader2, LogIn, UserPlus, ChevronUp, ChevronDown,
} from "lucide-react";
import { Link } from "wouter";
import type { CtaConfig } from "@shared/schema";

type ElementType = "trust_strip" | "enquiry_form" | "intro_request" | "score_gate" | "url_button" | "offer_card";

interface CtaElement {
  type: ElementType;
  config: Record<string, unknown>;
}

type AssignmentWithConfig = {
  id: string;
  ctaConfigId: string;
  contentType: string;
  contentId: string;
  displayOrder: number;
  isActive: boolean;
  ctaConfig: CtaConfig;
};

// ─── Individual element renderers ────────────────────────────────────────────

function TrustStrip({ config }: { config: Record<string, unknown> }) {
  const { hostName, hostPhoto, tagline } = config as { hostName: string; hostPhoto: string; tagline: string };
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/40 border">
      {hostPhoto ? (
        <img src={hostPhoto} alt={hostName} className="h-12 w-12 rounded-full object-cover shrink-0 border" />
      ) : (
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <User className="h-6 w-6 text-primary" />
        </div>
      )}
      <div className="min-w-0">
        <p className="font-semibold text-sm">{hostName}</p>
        {tagline && <p className="text-xs text-muted-foreground">{tagline}</p>}
      </div>
    </div>
  );
}

function EnquiryFormElement({ config, context }: { config: Record<string, unknown>; context: CtaPanelContext }) {
  const { formTitle, formSubtitle, submitLabel } = config as { formTitle: string; formSubtitle?: string; submitLabel: string };
  const { toast } = useToast();
  const [form, setForm] = useState({ fullName: "", email: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/enquiries", {
      ...form,
      radarEvaluationId: context.evaluationId ?? null,
      sourceUrl: window.location.href,
    }),
    onSuccess: () => { setSubmitted(true); },
    onError: (e: any) => toast({ title: e.message ?? "Submission failed", variant: "destructive" }),
  });

  if (submitted) {
    return (
      <div className="text-center py-6">
        <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
        <p className="font-medium text-sm">Enquiry sent!</p>
        <p className="text-xs text-muted-foreground">We'll be in touch soon.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <p className="font-semibold text-sm">{formTitle}</p>
        {formSubtitle && <p className="text-xs text-muted-foreground">{formSubtitle}</p>}
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Full Name</Label>
            <Input value={form.fullName} onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))} placeholder="Jane Smith" className="h-8 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@company.com" className="h-8 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Company (optional)</Label>
          <Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Acme Ltd" className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs">Message</Label>
          <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Tell us about your application..." rows={3} className="text-sm" />
        </div>
        <Button size="sm" className="w-full" onClick={() => mutation.mutate()} disabled={mutation.isPending || !form.fullName || !form.email} data-testid="button-enquiry-submit">
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageSquare className="h-4 w-4 mr-2" />}
          {submitLabel ?? "Send Enquiry"}
        </Button>
      </div>
    </div>
  );
}

function IntroRequestElement({ config, context }: { config: Record<string, unknown>; context: CtaPanelContext }) {
  const { messageTemplate, requireLogin } = config as { messageTemplate?: string; requireLogin?: boolean };
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [message, setMessage] = useState(messageTemplate ?? "I'd like to request an introduction.");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/introduction-requests", {
      message,
      radarEvaluationId: context.evaluationId ?? null,
      sourceUrl: window.location.href,
    }),
    onSuccess: () => { setSubmitted(true); },
    onError: (e: any) => toast({ title: e.message ?? "Failed to send request", variant: "destructive" }),
  });

  if (requireLogin && !isAuthenticated) {
    return (
      <div className="text-center py-4 space-y-3">
        <UserCheck className="h-8 w-8 text-primary mx-auto" />
        <div>
          <p className="font-medium text-sm">Sign in to request an introduction</p>
          <p className="text-xs text-muted-foreground">Create a free account or log in to send this request.</p>
        </div>
        <div className="flex gap-2 justify-center">
          <Button size="sm" asChild data-testid="button-intro-signup">
            <Link href={`/signup?redirect=${encodeURIComponent(window.location.pathname)}`}>
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />Sign Up
            </Link>
          </Button>
          <Button size="sm" variant="outline" asChild data-testid="button-intro-login">
            <Link href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}>
              <LogIn className="h-3.5 w-3.5 mr-1.5" />Log In
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="text-center py-6">
        <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
        <p className="font-medium text-sm">Introduction request sent!</p>
        <p className="text-xs text-muted-foreground">We'll coordinate and be in touch soon.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <UserCheck className="h-4 w-4 text-primary shrink-0" />
        <p className="font-semibold text-sm">Request an Introduction</p>
      </div>
      <div className="space-y-2">
        <Textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className="text-sm" />
        <Button size="sm" className="w-full" onClick={() => mutation.mutate()} disabled={mutation.isPending || !message.trim()} data-testid="button-intro-request-submit">
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
          Send Request
        </Button>
      </div>
    </div>
  );
}

function ScoreGateElement({ config, context }: { config: Record<string, unknown>; context: CtaPanelContext }) {
  const { threshold, lockedLabel, unlockedTitle, unlockedDescription, ctaLabel, ctaUrl } =
    config as { threshold: number; lockedLabel: string; unlockedTitle: string; unlockedDescription: string; ctaLabel: string; ctaUrl?: string };

  const score = context.score ?? context.evaluationScore ?? 0;
  const unlocked = score >= (threshold ?? 0);
  const [showEnquiry, setShowEnquiry] = useState(false);

  if (!unlocked) {
    return (
      <div className="flex items-center gap-3 py-3 px-4 rounded-lg bg-muted/40 border border-dashed">
        <Lock className="h-5 w-5 text-muted-foreground shrink-0" />
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{lockedLabel || "Complete your evaluation to unlock this content"}</p>
          {threshold > 0 && (
            <p className="text-xs text-muted-foreground/70">Requires score ≥ {threshold}% · Your score: {score}%</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
      <div>
        <Badge className="mb-2 text-xs bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-none">
          <CheckCircle2 className="h-3 w-3 mr-1" />Unlocked
        </Badge>
        <p className="font-semibold text-sm">{unlockedTitle}</p>
        {unlockedDescription && <p className="text-xs text-muted-foreground mt-0.5">{unlockedDescription}</p>}
      </div>
      {!showEnquiry && (
        <Button size="sm" className="w-full" onClick={() => ctaUrl ? window.open(ctaUrl, "_blank") : setShowEnquiry(true)} data-testid="button-score-gate-cta">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
          {ctaLabel ?? "Claim Offer"}
        </Button>
      )}
      {showEnquiry && (
        <EnquiryFormElement config={{ formTitle: ctaLabel ?? "Claim Offer", submitLabel: "Submit" }} context={context} />
      )}
    </div>
  );
}

function ensureProtocol(url: string): string {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function resolveContextUrl(raw: string | undefined | null, context: CtaPanelContext): string {
  if (!raw) return "";
  const resolved = raw
    .replace(/\{\{companyWebsite\}\}/g, context.companyWebsite ?? "")
    .replace(/\{\{radarProductLandingPageUrl\}\}/g, context.radarProductLandingPageUrl ?? "")
    .replace(/\{\{radarProductDatasheetUrl\}\}/g, context.radarProductDatasheetUrl ?? "")
    .replace(/\{\{articleLandingPageUrl\}\}/g, context.articleLandingPageUrl ?? "")
    .replace(/\{\{articleProductUrl\}\}/g, context.articleProductUrl ?? "")
    .replace(/\{\{articleDatasheetUrl\}\}/g, context.articleDatasheetUrl ?? "");
  return resolved ? ensureProtocol(resolved) : "";
}

function UrlButtonElement({ config, context }: { config: Record<string, unknown>; context: CtaPanelContext }) {
  const { label, url, style } = config as { label: string; url: string; style?: "primary" | "secondary" | "outline" };
  const resolvedUrl = resolveContextUrl(url, context);
  const variant = style === "outline" ? "outline" : style === "secondary" ? "secondary" : "default";
  return (
    <Button
      variant={variant}
      className="w-full"
      onClick={() => resolvedUrl && window.open(resolvedUrl, "_blank")}
      disabled={!resolvedUrl}
      data-testid="button-url-cta"
    >
      <ExternalLink className="h-4 w-4 mr-2" />
      {label || "Learn More"}
    </Button>
  );
}

function OfferCardElement({ config, context }: { config: Record<string, unknown>; context: CtaPanelContext }) {
  const { title, description, badgeLabel, ctaLabel, ctaUrl } =
    config as { title: string; description: string; badgeLabel?: string; ctaLabel: string; ctaUrl?: string };
  const [showEnquiry, setShowEnquiry] = useState(false);

  return (
    <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-background p-4 space-y-3">
      {badgeLabel && (
        <Badge variant="outline" className="text-xs border-primary/30 text-primary">
          <Gift className="h-3 w-3 mr-1" />{badgeLabel}
        </Badge>
      )}
      <div>
        <p className="font-semibold text-sm">{title}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      {!showEnquiry ? (
        <Button size="sm" className="w-full" onClick={() => ctaUrl ? window.open(ctaUrl, "_blank") : setShowEnquiry(true)} data-testid="button-offer-cta">
          <Gift className="h-3.5 w-3.5 mr-1.5" />
          {ctaLabel ?? "Claim"}
        </Button>
      ) : (
        <EnquiryFormElement config={{ formTitle: ctaLabel ?? "Claim Offer", submitLabel: "Submit" }} context={context} />
      )}
    </div>
  );
}

// ─── Element dispatcher ───────────────────────────────────────────────────────

function CtaElementRenderer({ element, context }: { element: CtaElement; context: CtaPanelContext }) {
  switch (element.type) {
    case "trust_strip": return <TrustStrip config={element.config} />;
    case "enquiry_form": return <EnquiryFormElement config={element.config} context={context} />;
    case "intro_request": return <IntroRequestElement config={element.config} context={context} />;
    case "score_gate": return <ScoreGateElement config={element.config} context={context} />;
    case "url_button": return <UrlButtonElement config={element.config} context={context} />;
    case "offer_card": return <OfferCardElement config={element.config} context={context} />;
    default: return null;
  }
}

// ─── Flatten + dedup helper ──────────────────────────────────────────────────

function flattenElements(assignments: AssignmentWithConfig[]): CtaElement[] {
  const flat: CtaElement[] = [];
  for (const a of assignments) {
    if (!a.isActive) continue;
    const els = (a.ctaConfig?.elements ?? []) as CtaElement[];
    for (const el of els) {
      // Collapse consecutive trust_strip duplicates (same hostName)
      const prev = flat[flat.length - 1];
      if (
        el.type === "trust_strip" &&
        prev?.type === "trust_strip" &&
        (prev.config as any).hostName === (el.config as any).hostName
      ) continue;
      flat.push(el);
    }
  }
  return flat;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface CtaPanelContext {
  /** Radar evaluation ID — passed to enquiry/intro-request submissions */
  evaluationId?: string;
  /** Numeric score 0-100 from a radar evaluation (alias: score) */
  evaluationScore?: number;
  /** Alias for evaluationScore — either is accepted */
  score?: number;
  /** Company name for context-aware copy */
  companyName?: string;
  /** Company website URL */
  companyWebsite?: string | null;
  /** Product/radar product name */
  productName?: string;
  /** Article title for context-aware copy */
  articleTitle?: string;
  /** Radar config: product landing page URL */
  radarProductLandingPageUrl?: string | null;
  /** Radar config: product datasheet URL */
  radarProductDatasheetUrl?: string | null;
  /** Article-linked landing page URL */
  articleLandingPageUrl?: string | null;
  /** Article-linked product URL */
  articleProductUrl?: string | null;
  /** Article-linked datasheet URL */
  articleDatasheetUrl?: string | null;
  /** Contact person display name */
  contactPersonName?: string | null;
  /** Contact person photo/avatar URL */
  contactPersonPhotoUrl?: string | null;
  /** Contact person job title */
  contactPersonTitle?: string | null;
}

interface CtaPanelProps {
  contentType: string;
  contentId: string;
  context?: CtaPanelContext;
  className?: string;
}

export function CtaPanel({ contentType, contentId, context = {}, className }: CtaPanelProps) {
  const { data: assignments, isLoading } = useQuery<AssignmentWithConfig[]>({
    queryKey: ["/api/public/cta-assignments", contentType, contentId],
    queryFn: () =>
      fetch(`/api/public/cta-assignments?contentType=${contentType}&contentId=${encodeURIComponent(contentId)}`)
        .then(r => r.json()),
    enabled: !!(contentType && contentId),
  });

  if (isLoading) {
    return (
      <div className={className}>
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  if (!assignments || assignments.length === 0) return null;

  const elements = flattenElements(assignments);
  if (elements.length === 0) return null;

  return (
    <Card className={className} data-testid="cta-panel">
      <CardContent className="pt-5 pb-5 space-y-4">
        {elements.map((el, i) => (
          <div key={i}>
            {i > 0 && <Separator className="my-3" />}
            <CtaElementRenderer element={el} context={context} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Admin CTA assignments panel ──────────────────────────────────────────────

interface CtaAssignmentsPanelProps {
  contentType: string;
  contentId: string;
}

export function CtaAssignmentsPanel({ contentType, contentId }: CtaAssignmentsPanelProps) {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState("");

  const { data: assignments = [], isLoading } = useQuery<AssignmentWithConfig[]>({
    queryKey: ["/api/admin/cta-assignments", contentType, contentId],
    queryFn: () =>
      fetch(`/api/admin/cta-assignments?contentType=${contentType}&contentId=${encodeURIComponent(contentId)}`)
        .then(r => r.json()),
    enabled: !!(contentType && contentId),
  });

  const { data: configs = [] } = useQuery<Array<CtaConfig & { usageCount: number }>>({
    queryKey: ["/api/admin/cta-configs"],
  });

  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["/api/admin/cta-assignments", contentType, contentId] });

  const addMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/cta-assignments", {
      contentType,
      contentId,
      ctaConfigId: selectedConfigId,
      displayOrder: assignments.length,
      isActive: true,
    }),
    onSuccess: () => {
      invalidate();
      setAdding(false);
      setSelectedConfigId("");
    },
    onError: (e: any) => toast({ title: e.message ?? "Failed to attach CTA", variant: "destructive" }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/cta-assignments/${id}`),
    onSuccess: invalidate,
    onError: (e: any) => toast({ title: e.message ?? "Failed to remove", variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiRequest("PATCH", `/api/admin/cta-assignments/${id}`, { isActive }),
    onSuccess: invalidate,
    onError: (e: any) => toast({ title: e.message ?? "Failed to update", variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => apiRequest("POST", "/api/admin/cta-assignments/reorder", { ids }),
    onSuccess: invalidate,
    onError: (e: any) => toast({ title: e.message ?? "Failed to reorder", variant: "destructive" }),
  });

  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const ids = assignments.map(a => a.id);
    [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
    reorderMutation.mutate(ids);
  };

  const moveDown = (idx: number) => {
    if (idx === assignments.length - 1) return;
    const ids = assignments.map(a => a.id);
    [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
    reorderMutation.mutate(ids);
  };

  const activeIds = new Set(assignments.map(a => a.ctaConfigId));
  const availableConfigs = configs.filter(c => !activeIds.has(c.id));

  if (isLoading) return <Skeleton className="h-20 w-full" />;

  return (
    <div className="space-y-3" data-testid="cta-assignments-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">CTA Library</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAdding(v => !v)} data-testid="button-attach-cta">
          + Attach CTA
        </Button>
      </div>

      {adding && (
        <div className="flex gap-2 items-center p-3 rounded-lg border bg-muted/30">
          <select
            className="flex-1 h-8 rounded border text-sm bg-background px-2"
            value={selectedConfigId}
            onChange={e => setSelectedConfigId(e.target.value)}
            data-testid="select-cta-config"
          >
            <option value="">— Pick a CTA config —</option>
            {availableConfigs.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          <Button size="sm" className="h-8" onClick={() => addMutation.mutate()} disabled={!selectedConfigId || addMutation.isPending} data-testid="button-confirm-attach-cta">
            {addMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}

      {assignments.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground py-2">No CTAs attached. Click "Attach CTA" to add one from the library.</p>
      )}

      <div className="space-y-2">
        {assignments.map((a, idx) => (
          <div key={a.id} className="flex items-center gap-2 p-2 rounded border bg-card text-sm" data-testid={`cta-assignment-${a.id}`}>
            <div className="flex flex-col gap-0.5 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0"
                onClick={() => moveUp(idx)}
                disabled={idx === 0 || reorderMutation.isPending}
                data-testid={`button-move-up-cta-${a.id}`}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0"
                onClick={() => moveDown(idx)}
                disabled={idx === assignments.length - 1 || reorderMutation.isPending}
                data-testid={`button-move-down-cta-${a.id}`}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-xs truncate">{a.ctaConfig.title}</p>
              <p className="text-xs text-muted-foreground">{((a.ctaConfig.elements ?? []) as CtaElement[]).length} elements · order {a.displayOrder}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 text-xs ${a.isActive ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}`}
                onClick={() => toggleMutation.mutate({ id: a.id, isActive: !a.isActive })}
                data-testid={`button-toggle-cta-${a.id}`}
              >
                {a.isActive ? "Active" : "Inactive"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                onClick={() => removeMutation.mutate(a.id)}
                data-testid={`button-remove-cta-${a.id}`}
              >
                ×
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
